import { createPKCE, randomState } from "./pkce";


type OAuthPopupOptions = {
  authEndpoint: string; // e.g., "https://accounts.example.com/oauth2/v2/auth"
  clientId: string;
  scope: string; // space-delimited
  redirectUri: string; // e.g., `${window.location.origin}/oauth-popup-callback.html`
  extraParams?: Record<string, string | number | boolean | undefined>; // e.g., { prompt: "consent", access_type: "offline" }
  timeoutMs?: number; // optional timeout for the flow
};

export type OAuthPopupResult =
  | { ok: true; code: string; state: string; codeVerifier: string }
  | { ok: false; error: string };

const MESSAGE_TYPE = "oauth:callback";
const STORAGE_KEY = (state: string) => `pkce:${state}`;

function toQuery(params: Record<string, string>): string {
  const usp = new URLSearchParams(params);
  return usp.toString();
}

function openCenteredPopup(url: string, title = "Sign in", w = 500, h = 600): Window | null {
  const dualScreenLeft = window.screenLeft !== undefined ? window.screenLeft : window.screenX;
  const dualScreenTop = window.screenTop !== undefined ? window.screenTop : window.screenY;

  const width = window.innerWidth || document.documentElement.clientWidth || window.screen.width;
  const height = window.innerHeight || document.documentElement.clientHeight || window.screen.height;

  const systemZoom = width / window.screen.availWidth;
  const left = (width - w) / 2 / systemZoom + dualScreenLeft;
  const top = (height - h) / 2 / systemZoom + dualScreenTop;

  const features = [
    `scrollbars=yes`,
    `resizable=yes`,
    `width=${Math.floor(w / systemZoom)}`,
    `height=${Math.floor(h / systemZoom)}`,
    `top=${Math.floor(top)}`,
    `left=${Math.floor(left)}`,
  ].join(",");

  return window.open(url, "oauth2_login_popup", features);
}

export async function openOAuth2Popup(opts: OAuthPopupOptions): Promise<OAuthPopupResult> {
  const { authEndpoint, clientId, scope, extraParams, timeoutMs = 3 * 60_000 } = opts;

  // Generate anti-CSRF state and PKCE
  const state = randomState(24);
  const { verifier, challenge } = await createPKCE();

  // Persist code_verifier keyed by state so we can retrieve after callback
  try {
    localStorage.setItem(STORAGE_KEY(state), verifier);
  } catch {}

  const baseParams: Record<string, string> = {
    response_type: "code",
    client_id: clientId,
    scope,
    state,
    code_challenge: challenge,
    code_challenge_method: "S256",
  };

  if (extraParams) {
    for (const [k, v] of Object.entries(extraParams)) {
      if (v !== undefined && v !== null) baseParams[k] = String(v);
    }
  }

  const url = `${authEndpoint}?${toQuery(baseParams)}`;
  const popup = openCenteredPopup(url, "Sign in");

  if (!popup) {
    return { ok: false, error: "PopupBlocked" };
  }

  // Promise that resolves when we receive the callback message
  return new Promise<OAuthPopupResult>((resolve) => {
    const origin = window.location.origin;
    let timer: number | undefined;
    let closedPoll: number | undefined;

    const cleanup = () => {
      window.removeEventListener("message", onMessage);
      if (timer) window.clearTimeout(timer);
      if (closedPoll) window.clearInterval(closedPoll);
      try {
        popup.close();
      } catch {}
    };

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== origin) return;
      const data = event.data || {};
      if (data.type !== MESSAGE_TYPE) return;

      const params = data.params || {};
      const receivedState = params.state;
      const code = params.code;
      const error = params.error || params.error_description;

      if (!receivedState || receivedState !== state) {
        cleanup();
        resolve({ ok: false, error: "StateMismatch" });
        return;
      }

      if (error) {
        cleanup();
        resolve({ ok: false, error: String(error) });
        return;
      }

      if (!code) {
        cleanup();
        resolve({ ok: false, error: "MissingCode" });
        return;
      }

      // Retrieve code_verifier and clear it
      let codeVerifier = "";
      try {
        const key = STORAGE_KEY(state);
        codeVerifier = localStorage.getItem(key) || "";
        localStorage.removeItem(key);
      } catch {}

      cleanup();
      resolve({ ok: true, code, state, codeVerifier });
    };

    window.addEventListener("message", onMessage);

    // Handle popup closed by user
    closedPoll = window.setInterval(() => {
      if (popup.closed) {
        window.clearInterval(closedPoll);
        window.removeEventListener("message", onMessage);
        resolve({ ok: false, error: "PopupClosed" });
      }
    }, 300);

    // Timeout
    timer = window.setTimeout(() => {
      cleanup();
      resolve({ ok: false, error: "Timeout" });
    }, timeoutMs);
  });
}